// Store original fetch
if (!window.originalFetch) {
  window.originalFetch = window.fetch;
}

function parseCollectionData() {
  try {
    const collectionDataElement = document.querySelector('[js-collections-data]');
    if (!collectionDataElement || !collectionDataElement.innerHTML) {
      console.warn('Collection data element not found or empty');
      return [];
    }
    const data = JSON.parse(collectionDataElement.innerHTML);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.warn('Error parsing collection data:', error);
    return [];
  }
}

// Utility function to convert strings to handle format (lowercase with dashes)
function handleize(str) {
  return str
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '');
}

class CollectionManager {
  constructor() {
    this.collections = new Map(); // Cache for loaded collections
    this.loadingPromises = new Map(); // Track in-flight requests
  }

  async fetchAllProducts(handle) {
    // Return cached collection if we have it
    if (this.collections.has(handle)) {
      return this.collections.get(handle);
    }

    // Return existing promise if we're already loading this collection
    if (this.loadingPromises.has(handle)) {
      return this.loadingPromises.get(handle);
    }

    // Start new request
    const loadPromise = this.fetchCollection(handle);
    this.loadingPromises.set(handle, loadPromise);

    try {
      const collection = await loadPromise;
      this.collections.set(handle, collection);
      return collection;
    } finally {
      this.loadingPromises.delete(handle);
    }
  }

  async fetchCollection(handle) {
    const response = await fetch(`/collections/${handle}?view=json`);
    if (!response.ok) {
      throw new Error(`Failed to load collection: ${response.statusText}`);
    }
    const text = await response.text();

    try {
      // Create a temporary DOM element to parse the HTML response
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const scriptElement = doc.querySelector('script[js-collection-data]');

      if (!scriptElement) {
        throw new Error('Could not find JSON script element in response');
      }

      const json = JSON.parse(scriptElement.innerHTML);
      return json;
    } catch (error) {
      console.error('Failed to parse JSON:', { text, error });
      throw new Error('Failed to parse collection data');
    }
  }

  getProducts(collection) {
    // Implementation of getProducts method
  }
}

class BundleBoard extends HTMLElement {
  constructor() {
    super();

    this.collectionManager = new CollectionManager();
    this.state = {
      age: null,
      theme: null,
      isCollectionOpen: false,
      activeCollection: null,
      cards: this.parseCardsStructure(),
      currentBoard: null,
    };

    // Bind event handlers once in constructor
    this.handleToggleChange = this.handleToggleChange.bind(this);
    this.handleCartToggle = this.handleCartToggle.bind(this);
    this.handleVariantAdded = this.handleVariantAdded.bind(this);
    this.toggleCollectionSidebar = this.toggleCollectionSidebar.bind(this);
    this.updateCheckboxVisibility = this.updateCheckboxVisibility.bind(this);

    // Initialize everything in a specific order
    this.renderToggles();
    this.setupListeners();
    this.initializeState();
    this.initializeBundleCollectionToggles();

    this.collectionData = parseCollectionData();

    // Add collections that get discounts
    this.discountCollections = this.collectionData.filter((collection) => collection.eligible_for_discounts);

    // Get crib/bed collections
    const cribOrBedDataElement = document.querySelector('[js-crib-or-bed-data]');
    this.cribOrBedCollections = cribOrBedDataElement ? JSON.parse(cribOrBedDataElement.textContent) : [];

    // Add initial crib/bed to cart after a short delay to ensure everything is initialized
    setTimeout(() => this.addInitialCribOrBed(), 100);

    // Check URL parameters after initialization
    this.checkUrlParameters();
  }

  initialize() {
    this.state.cards = this.parseCardsStructure();
    this.renderToggles();
    this.initializeState();
    this.initializeBundleCollectionToggles();

    this.collectionData = parseCollectionData();

    // Add collections that get discounts
    this.discountCollections = this.collectionData.filter((collection) => collection?.eligible_for_discounts);

    // Get crib/bed collections
    const cribOrBedDataElement = document.querySelector('[js-crib-or-bed-data]');
    this.cribOrBedCollections = cribOrBedDataElement ? JSON.parse(cribOrBedDataElement.textContent) : [];

    // Add initial crib/bed to cart after a short delay to ensure everything is initialized
    setTimeout(() => this.addInitialCribOrBed(), 100);

    // Check URL parameters after initialization
    this.checkUrlParameters();
  }

  // Updated method to check URL parameters with proper decoding
  checkUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);

    // Get and decode parameters - use "design" instead of "theme"
    const ageParam = urlParams.get('age') ? decodeURIComponent(urlParams.get('age')) : null;
    const themeParam = urlParams.get('design') ? decodeURIComponent(urlParams.get('design')) : null;

    if (ageParam || themeParam) {
      this.applyUrlParameters(ageParam, themeParam);
    }
  }

  // Updated method to apply URL parameters with case-insensitive matching
  applyUrlParameters(ageParam, themeParam) {
    const ages = this.getAges();

    // Find matching age (case-insensitive)
    const matchedAge = ageParam ? ages.find((age) => age.toLowerCase() === ageParam.toLowerCase()) : null;

    if (matchedAge) {
      // Find the age toggle and check it
      const ageToggle = this.querySelector(`[data-type="age"][data-value="${matchedAge}"]`);
      if (ageToggle) {
        ageToggle.checked = true;

        // Get themes for this age
        const themes = this.getThemesForAge(matchedAge);

        // Find matching theme (case-insensitive)
        const matchedTheme = themeParam
          ? themes.find((theme) => theme.toLowerCase() === themeParam.toLowerCase())
          : null;

        if (matchedTheme) {
          // Update theme toggles first since age change will reset them
          this.setState({ age: matchedAge });

          // Re-render theme toggles for this age
          const themeTogglesContainer = this.querySelector('[js-theme-toggles]');
          if (themeTogglesContainer) {
            themeTogglesContainer.innerHTML = this.renderThemeToggles(matchedAge, themes);
            this.setupListeners(); // Reattach listeners for new theme toggles
          }

          // Now select the theme toggle
          setTimeout(() => {
            const themeToggle = this.querySelector(`[data-type="theme"][data-value="${matchedTheme}"]`);
            if (themeToggle) {
              themeToggle.checked = true;

              // Update state and render
              const currentBoard = this.getBoard(matchedAge, matchedTheme);
              this.setState({
                age: matchedAge,
                theme: matchedTheme,
                currentBoard,
              });
            }
          }, 0);
        } else {
          // If theme not specified or invalid, just trigger the age change
          ageToggle.dispatchEvent(new Event('change'));
        }
      }
    }
  }

  // Add a helper method to generate URLs with encoded parameters
  generateBundleUrl(age, theme) {
    const url = new URL(window.location.href);
    url.searchParams.set('age', encodeURIComponent(age));

    if (theme) {
      // Use "design" instead of "theme"
      url.searchParams.set('design', encodeURIComponent(theme));
    }

    return url.toString();
  }

  // Add new method to handle adding initial crib/bed
  async addInitialCribOrBed() {
    if (!this.state.currentBoard) {
      return;
    }

    // Find the crib or bed item in the current board
    const cribOrBedItem = this.state.currentBoard.find(
      (item) =>
        item.collection &&
        ['cribs', 'beds', 'toddler-kids-twin-beds', 'cribs-bundle-collection'].includes(item.collection.handle)
    );

    if (!cribOrBedItem) {
      return;
    }

    // Skip if variant doesn't exist or is null (handles deleted/unavailable variants)
    if (!cribOrBedItem.variant || !cribOrBedItem.variant.id) {
      return;
    }

    // Get the bundle cart
    const cart = this.querySelector('bundle-cart');

    if (!cart) {
      return;
    }

    // Create variant object from the board item
    const variantObject = {
      id: String(cribOrBedItem.variant.id),
      image: cribOrBedItem.variant_image,
      title: cribOrBedItem.variant.title,
      price: (parseFloat(cribOrBedItem.variant.price) / 100).toFixed(2),
      product_title: cribOrBedItem.product.title,
      product_id: cribOrBedItem.product.id,
      display_card: cribOrBedItem.product.display_card || '',
      shipping: cribOrBedItem.shipping || 'Standard',
      eta: cribOrBedItem.eta || '',
    };

    // Dispatch variantAdded event
    this.dispatchEvent(
      new CustomEvent('variantAdded', {
        bubbles: true,
        detail: {
          variantObject,
          collectionValue: cribOrBedItem.collection.handle,
          isAutomatic: true,
        },
      })
    );

    this.renderBundleCards();
  }

  disconnectedCallback() {
    // Remove toggle listeners
    const toggles = this.querySelectorAll('[js-toggle]');
    toggles.forEach((toggle) => {
      toggle.removeEventListener('change', this.handleToggleChange);
    });

    // Remove cart toggle listeners
    const cartToggles = this.querySelectorAll('[js-bundle-cart-toggle]');
    cartToggles.forEach((toggle) => {
      toggle.removeEventListener('click', this.handleCartToggle);
    });

    // Remove variant added listener
    this.removeEventListener('variantAdded', this.handleVariantAdded);

    // Remove collection toggle listeners
    const collectionToggles = this.querySelectorAll('[js-bundle-collection-toggle]');
    collectionToggles.forEach((toggle) => {
      toggle.removeEventListener('click', this.toggleCollectionSidebar);
    });

    // Remove overlay listener
    const overlay = this.querySelector('[js-bundle-collection-overlay]');
    if (overlay) {
      overlay.removeEventListener('click', this.toggleCollectionSidebar);
    }

    // Remove close button listener
    const closeButton = this.querySelector('[js-bundle-collection-close]');
    if (closeButton) {
      closeButton.removeEventListener('click', this.toggleCollectionSidebar);
    }

    // Remove cart updated listener
    document.removeEventListener('cartUpdated', this.updateCheckboxVisibility);

    // Clear any stored data
    this.collectionManager.collections.clear();
    this.collectionManager.loadingPromises.clear();
    this.state = null;

    // Clean up event listener
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initializeState() {
    const ages = this.getAges();

    if (ages.length > 0) {
      const firstAge = ages[0];
      const themes = this.getThemesForAge(firstAge);

      // Get theme order data
      const themeDataElement = document.querySelector('[js-themes-data]');
      const themeData = themeDataElement ? JSON.parse(themeDataElement.textContent) : [];
      const themeOrderMap = new Map(themeData.map((theme) => [theme.title, theme.order]));

      // Sort themes and find the one with smallest order (excluding Create +)
      const sortedThemes = [...themes].sort((a, b) => {
        if (a === 'Create +') return 1;
        if (b === 'Create +') return -1;
        return (themeOrderMap.get(a) || 999) - (themeOrderMap.get(b) || 999);
      });
      const defaultTheme = sortedThemes.find((theme) => theme !== 'Create +');

      const currentBoard = this.getBoard(firstAge, defaultTheme);

      this.state = {
        ...this.state,
        age: firstAge,
        theme: defaultTheme,
        currentBoard,
      };

      // Only render the cards - event binding is already done in constructor
      this.renderBundleCards();
    }
  }

  setupListeners() {
    // Add event listeners for toggles
    const toggles = this.querySelectorAll('[js-toggle]');
    toggles.forEach((toggle) => {
      toggle.addEventListener('change', this.handleToggleChange);
    });

    // Add event listeners for cart toggles
    const cartToggles = this.querySelectorAll('[js-bundle-cart-toggle]');
    cartToggles.forEach((toggle) => {
      toggle.addEventListener('click', this.handleCartToggle);
    });

    // Add variant added listener
    this.addEventListener('variantAdded', this.handleVariantAdded);

    // Add cart updated listener
    document.addEventListener('cartUpdated', this.updateCheckboxVisibility);
  }

  async toggleCollectionSidebar(event) {
    const sidebar = this.querySelector('[js-bundle-collection]');
    const overlay = this.querySelector('[js-bundle-collection-overlay]');
    const closeButton = event?.target.closest('[js-bundle-collection-close]');

    // Always close if overlay or close button is clicked
    if (event?.target === overlay || closeButton) {
      this.closeSidebar();
      return;
    }

    // Handle opening via collection toggle
    const clickedElement = event.target.closest('[js-bundle-collection-toggle]');
    if (clickedElement && sidebar && overlay) {
      // Save the currently focused element before opening
      this.lastFocusedElement = document.activeElement;

      // Remove aria-hidden before showing the sidebar
      sidebar.removeAttribute('aria-hidden');

      sidebar.classList.add('active');
      overlay.classList.add('active');

      const clickedCollection = clickedElement.dataset.collection;
      if (clickedCollection) {
        try {
          const data = await this.collectionManager.fetchAllProducts(clickedCollection);
          await this.loadCollection(clickedCollection);

          // Set focus to the first focusable element in the sidebar
          this.setFocusToFirstElement(sidebar);
        } catch (error) {
          console.error('Error loading collection:', error);
        }
      }
    }
  }

  closeSidebar() {
    const sidebar = this.querySelector('[js-bundle-collection]');
    const overlay = this.querySelector('[js-bundle-collection-overlay]');
    const lastFocusedElement = this.lastFocusedElement;

    if (sidebar) {
      sidebar.classList.remove('active');
      // Set aria-hidden when hiding the sidebar
      sidebar.setAttribute('aria-hidden', 'true');
    }
    if (overlay) overlay.classList.remove('active');

    // Return focus to the element that was focused before opening the sidebar
    if (lastFocusedElement) {
      lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
  }

  // Add a helper method to find and focus the first focusable element
  setFocusToFirstElement(container) {
    // Save the currently focused element to return to later
    this.lastFocusedElement = document.activeElement;

    // Find all focusable elements
    const focusableElements = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    // Focus the first element (often this would be a close button)
    if (focusableElements.length > 0) {
      setTimeout(() => {
        focusableElements[0].focus();
      }, 100); // Small delay to ensure the DOM is ready
    }
  }

  async loadCollection(collection) {
    const productsContainer = this.querySelector('[js-bundle-collection-products]');
    const titleElement = this.querySelector('[js-bundle-collection-title]');
    const collectionToggles = this.querySelectorAll(`[js-bundle-collection-toggle]`);
    const collectionTogglesSelected = this.querySelectorAll(
      `[js-bundle-collection-toggle][data-collection="${collection}"]`
    );

    if (!productsContainer) return;

    try {
      // Wait for collection data to be loaded
      const collectionData = await this.collectionManager.fetchAllProducts(collection);
      if (!collectionData || !collectionData.products) {
        return;
      }

      // Update the collection title
      if (titleElement) {
        titleElement.textContent = collectionData.title || 'Bundle Builder';
      }

      collectionToggles.forEach((toggle) => {
        toggle.classList.remove('active');
      });

      collectionTogglesSelected.forEach((toggle) => {
        toggle.classList.add('active');
      });

      // Call renderProducts with the collection data and handle
      this.renderProducts(collectionData.products, collection);
      this.updateCheckboxVisibility();
    } catch (error) {
      productsContainer.innerHTML = `
        <div class="nb-bundle-collection-error">
          <p>Failed to load products. Please try again.</p>
        </div>
      `;
    }
  }

  renderProducts(products, collection) {
    const container = this.querySelector('[js-bundle-collection-products]');
    if (!container) return;

    // Get cart and items at render time
    const cart = this.querySelector('bundle-cart');
    const cartItems = cart ? Array.from(cart.items.values()) : [];

    container.innerHTML = products
      .map((product) => {
        // Check if this product/variant is in cart for this collection
        const isInCart = cartItems.some(
          (item) =>
            item.collectionValue === collection &&
            (item.variantObject.id === product.variants[0].id || item.variantObject.product_id === product.id)
        );

        // Add checkbox visibility class based on cart state
        const checkboxClass = isInCart ? 'is-visible' : '';

        // Add discount badge logic
        const isEligibleForDiscount = this.discountCollections?.some((col) => col.handle === collection);

        // Base discount on total items in cart
        const totalItemsInCart = cartItems.length;

        let discountText = '';
        if (isEligibleForDiscount) {
          if (totalItemsInCart === 0) {
            discountText = '10% off'; // Show 10% when cart is empty
          } else if (totalItemsInCart === 1) {
            discountText = '10% off'; // Show 10% when there's 1 item
          } else if (totalItemsInCart >= 2) {
            discountText = '15% off'; // Show 15% when there are 2 or more items
          }
        }

        // Handle "Break out variants into separate cards" display mode
        if (product.display_card === 'Break out variants into separate cards') {
          return product.variants
            .map(
              (variant) => `                
              <bundle-product data-product-id="${product.id}" data-variant-id="${variant.id}" class="${
                this.state.currentBoard?.some(
                  (item) => item.product?.id === product.id && (!item.variant?.id || item.variant.id === variant.id)
                )
                  ? 'preselected'
                  : ''
              }" data-display-card="${product.display_card}">
                <div class="nb-bundle-product-preselected">
                  ${this.state.theme}
                </div>
                <div class="nb-bundle-checkbox ${checkboxClass}" js-bundle-checkbox data-collection="${collection}" data-size="small">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="24" height="24" rx="12" fill="#173482"/>
                    <path d="M17.5 7.875L9.9375 15.4375L6.5 12" stroke="#F9F5F2" stroke-width="1.54688" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>

                <form method="post" class="nb-bundle-collection-item">
                  <input type="hidden" value="${product.variants[0].options[0]}" name="bundle-item-color-${
                product.id
              }-${collection}" />

                  ${product.variants
                    .map(
                      (variant) =>
                        `
                      <input type="hidden" value="${
                        variant.image_bundle || variant.featured_image?.src || product.featured_image
                      }" name="bundle-item-image-${variant.id}-${collection}" />
                      <input type="hidden" value="${variant.title}" name="bundle-item-variant-title-${
                          variant.id
                        }-${collection}" />
                      <input type="hidden" value="${variant.price / 100}" name="bundle-item-variant-price-${
                          variant.id
                        }-${collection}" />
                      <input type="hidden" value="${product.title}" name="bundle-item-product-title-${
                          variant.id
                        }-${collection}" />
                      <input type="hidden" value="${
                        product.variants_metafields.find((v) => v.id === variant.id).shipping
                      }" name="bundle-item-variant-shipping-${variant.id}-${collection}" />
                      <input type="hidden" value="${
                        product.variants_metafields.find((v) => v.id === variant.id).eta
                      }" name="bundle-item-variant-eta-${variant.id}-${collection}" />
                    `
                    )
                    .join('')}

                  <input type="hidden" value="${product.display_card}" name="bundle-display-card" />

                  <div class="nb-bundle-collection-item-image">
                    <div class="nb-bundle-collection-item-badge nb-body-copy-11-medium">
                      <span js-bundle-discount data-collection="${collection}">${discountText}</span>
                    </div>
                    <a href="/products/${product.handle}" js-image target="_blank">
                      <img src="${
                        variant.image_bundle || variant.featured_image?.src || product.featured_image
                      }&width=300" 
                           alt="${product.title} - ${variant.title}" 
                           width="400" 
                           height="500">
                    </a>
                  </div>
                  
                  <div class="nb-bundle-collection-item-details">
                    <div class="nb-bundle-collection-item-title">
                      <h3 class="nb-body-copy-13-medium">${product.title}</h3>
                      <p class="nb-body-copy-12">${variant.title}</p>
                    </div>
                    
                    ${
                      product.type == 'Curtain'
                        ? generateQuantitySelector(product.id)
                        : generateStandardQuantitySelector(product.id)
                    }

                    ${
                      // Show crib discount message if this is a crib collection
                      this.cribOrBedCollections.includes(collection)
                        ? `<p class='nb-body-copy-11-regular nb-bundle-collection-crib-discount' style='margin-top: 4px; display: none;'>15% off second crib</p>`
                        : ''
                    }

                    <div class="nb-bundle-collection-item-price">
                      <div class="nb-body-copy-13" js-variant-price data-price="${variant.price}">
                        $${(variant.price / 100).toFixed(2)}
                      </div>
                      <input type="hidden" value="${product.id}" name="product">
                      <input type="hidden" name="bundle-variant-${product.id}-${collection}" value="${variant.id}">
                      <input type="hidden" name="collection" value="${collection}">
                      <button type="submit" class="nb-body-copy-12-medium nb-bundle-collection-item-button">
                        Add to bundle
                      </button>
                    </div>
                  </div>
                </form>
                <script type='application/json'>
                  ${JSON.stringify(product)}
                </script>
              </bundle-product>
            `
            )
            .join('');
        }
        // Handle "Break out variants into separate colors" display mode
        else if (product.display_card === 'Break out variants into separate colors') {
          const colorOptionIndex = product.options.findIndex((option) => option.toLowerCase() === 'color');

          if (colorOptionIndex !== -1) {
            const uniqueColors = [...new Set(product.variants.map((variant) => variant.options[colorOptionIndex]))];

            return uniqueColors
              .map((color) => {
                const colorVariants = product.variants.filter((variant) => variant.options[colorOptionIndex] === color);
                // Check if ANY variants of this color are available
                const hasAvailableVariants = colorVariants.some((variant) => variant.available);
                // Get primary variant (either first available one or just first one if none available)
                const primaryVariant = colorVariants.find((v) => v.available) || colorVariants[0];
                const uniqueId = `${product.id}-${primaryVariant.id}-${handleize(color)}`;

                return `
                  <bundle-product data-product-id="${product.id}" data-variant-id="${primaryVariant.id}" class="${
                  this.state.currentBoard?.some(
                    (item) =>
                      item.product?.id === product.id && (!item.variant?.id || item.variant.id === primaryVariant.id)
                  )
                    ? 'preselected'
                    : ''
                }" data-display-card="${product.display_card}">
                    <div class="nb-bundle-product-preselected">
                      ${this.state.theme}
                    </div>

                    <div class="nb-bundle-checkbox ${checkboxClass}" js-bundle-checkbox data-collection="${collection}" data-size="small">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="24" height="24" rx="12" fill="#173482"/>
                        <path d="M17.5 7.875L9.9375 15.4375L6.5 12" stroke="#F9F5F2" stroke-width="1.54688" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>

                    <form method="post" class="nb-bundle-collection-item">
                      <div class="product-form__input" js-option="Color" style="display: none;">
                        <input type="radio" 
                               value="${color}" 
                               name="bundle-item-color-${product.id}-${collection}"
                               checked
                               style="display: none;"
                               js-variant-option-color />
                      </div>

                    ${product.variants
                      .map(
                        (variant) =>
                          `
                        <input type="hidden" value="${
                          variant.image_bundle || variant.featured_image?.src || product.featured_image
                        }" name="bundle-item-image-${variant.id}-${collection}" />
                        <input type="hidden" value="${variant.title}" name="bundle-item-variant-title-${
                            variant.id
                          }-${collection}" />
                        <input type="hidden" value="${variant.price / 100}" name="bundle-item-variant-price-${
                            variant.id
                          }-${collection}" />
                        <input type="hidden" value="${product.title}" name="bundle-item-product-title-${
                            variant.id
                          }-${collection}" />
                        <input type="hidden" value="${
                          product.variants_metafields.find((v) => v.id === variant.id).shipping
                        }" name="bundle-item-variant-shipping-${variant.id}-${collection}" />
                        <input type="hidden" value="${
                          product.variants_metafields.find((v) => v.id === variant.id).eta
                        }" name="bundle-item-variant-eta-${variant.id}-${collection}" />
                      `
                      )
                      .join('')}

                      <div class="nb-bundle-collection-item-image">
                        <div class="nb-bundle-collection-item-badge nb-body-copy-11-medium">
                          <span js-bundle-discount data-collection="${collection}">${discountText}</span>
                        </div>
                        <a href="/products/${product.handle}" js-image target="_blank">
                          <img src="${
                            primaryVariant.image_bundle || primaryVariant.featured_image?.src || product.featured_image
                          }&width=300" 
                               alt="${product.title} - ${color}" 
                               width="400" 
                               height="500">
                        </a>
                      </div>
                      
                      <div class="nb-bundle-collection-item-details">
                        <div class="nb-bundle-collection-item-title">
                          <h3 class="nb-body-copy-13-medium">${product.title} in ${color}</h3>
                        </div>
                        
                        <div class="nb-bundle-collection-item-options">
                          ${product.options
                            .filter((option) => option.toLowerCase() !== 'color')
                            .map((optionName, optionIndex) => {
                              const actualIndex = optionIndex >= colorOptionIndex ? optionIndex + 1 : optionIndex;
                              const optionValues = new Set(colorVariants.map((v) => v.options[actualIndex]));

                              return this.generateOptionFieldset(
                                optionName,
                                Array.from(optionValues),
                                colorVariants,
                                product,
                                collection,
                                uniqueId // Pass the unique identifier to the fieldset generator
                              );
                            })
                            .join('')}
                          
                          <select name="bundle-variant-${uniqueId}-${collection}" 
                                  id="variant-select-${uniqueId}-${collection}" 
                                  style="display: none">
                            ${colorVariants
                              .map(
                                (variant) => `
                                <option 
                                  value="${variant.id}" 
                                  title="${variant.title}"
                                  data-price="${variant.price}"
                                  data-compare-price=""
                                  data-image="${variant.image_bundle || variant.featured_image?.src || ''}"
                                  ${variant.id === primaryVariant.id ? 'selected' : ''}
                                >
                                  ${variant.title} - $${(variant.price / 100).toFixed(2)}
                                </option>
                              `
                              )
                              .join('')}
                          </select>
                        </div>

                        ${
                          product.type == 'Curtain'
                            ? generateQuantitySelector(uniqueId)
                            : generateStandardQuantitySelector(uniqueId)
                        }

                        ${
                          // Show crib discount message if this is a crib collection
                          this.cribOrBedCollections.includes(collection)
                            ? `<p class='nb-body-copy-11-regular nb-bundle-collection-crib-discount' style='margin-top: 4px; display: none;'>15% off second crib</p>`
                            : ''
                        }

                        <div class="nb-bundle-collection-item-price">
                          <div class="nb-body-copy-13" js-variant-price data-price="${primaryVariant.price}">
                            $${(primaryVariant.price / 100).toFixed(2)}
                          </div>
                          <input type="hidden" value="${product.id}" name="product">
                          <input type="hidden" name="collection" value="${collection}">
                          <button type="submit" class="nb-body-copy-12-medium nb-bundle-collection-item-button" ${
                            !hasAvailableVariants ? 'disabled' : ''
                          }>
                            ${hasAvailableVariants ? 'Add to bundle' : 'Sold out'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </bundle-product>
                `;
              })
              .join('');
          }
        }
        // Default display mode
        else {
          return `
            <bundle-product data-product-id="${product.id}" class="${
            this.state.currentBoard?.some((item) => item.product?.id === product.id) ? 'preselected' : ''
          }"  data-display-card="${product.display_card}">
              <div class="nb-bundle-product-preselected">
                <div>
                  ${this.state.theme}
                </div>
                <div>
                  ${this.state.age}
                </div>
              </div>
              <div class="nb-bundle-checkbox ${checkboxClass}" js-bundle-checkbox data-collection="${collection}" data-size="small">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="12" fill="#173482"/>
                  <path d="M17.5 7.875L9.9375 15.4375L6.5 12" stroke="#F9F5F2" stroke-width="1.54688" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>

              <form method="post" class="nb-bundle-collection-item">
              ${product.variants
                .map(
                  (variant) =>
                    `
                  <input type="hidden" value="${
                    variant.image_bundle || variant.featured_image?.src || product.featured_image
                  }" name="bundle-item-image-${variant.id}-${collection}" />
                  <input type="hidden" value="${variant.title}" name="bundle-item-variant-title-${
                      variant.id
                    }-${collection}" />
                  <input type="hidden" value="${variant.price / 100}" name="bundle-item-variant-price-${
                      variant.id
                    }-${collection}" />
                  <input type="hidden" value="${product.title}" name="bundle-item-product-title-${
                      variant.id
                    }-${collection}" />
                    <input type="hidden" value="${
                      product.variants_metafields.find((v) => v.id === variant.id).shipping
                    }" name="bundle-item-variant-shipping-${variant.id}-${collection}" />
                    <input type="hidden" value="${
                      product.variants_metafields.find((v) => v.id === variant.id).eta
                    }" name="bundle-item-variant-eta-${variant.id}-${collection}" />
                `
                )
                .join('')}
                <div class="nb-bundle-collection-item-image">
                  <div class="nb-bundle-collection-item-badge nb-body-copy-11-medium">
                    <span js-bundle-discount data-collection="${collection}">${discountText}</span>
                  </div>
                  <a href="/products/${product.handle}" js-image target="_blank">
                    <img src="${
                      product.variants[0].image_bundle ||
                      product.variants[0].featured_image?.src ||
                      product.featured_image
                    }&width=300" alt="${product.title}" width="400" height="500">
                  </a>
                </div>
                
                <div class="nb-bundle-collection-item-details">
                  <div class="nb-bundle-collection-item-title">
                    <h3 class="nb-body-copy-13-medium">${product.title}</h3>
                  </div>
                  
                  <div class="nb-bundle-collection-item-options">
                    ${product.options
                      .map((optionName, optionIndex) => {
                        const optionValues = new Set(product.variants.map((variant) => variant.options[optionIndex]));
                        return this.generateOptionFieldset(
                          optionName,
                          Array.from(optionValues),
                          product.variants,
                          product,
                          collection
                        );
                      })
                      .join('')}
                    
                    <select name="bundle-variant-${product.id}-${collection}" id="variant-select-${
            product.id
          }-${collection}" style="display: none">
                      ${product.variants
                        .map(
                          (variant) => `
                          <option 
                            value="${variant.id}" 
                            title="${variant.title}"
                            data-price="${variant.price}"
                            data-compare-price=""
                            data-image="${variant.image_bundle || variant.featured_image?.src || ''}"
                            ${variant.available ? 'selected' : ''}
                          >
                            ${variant.title} - $${(variant.price / 100).toFixed(2)}
                          </option>
                        `
                        )
                        .join('')}
                    </select>
                  </div>

                  ${
                    product.type == 'Curtain'
                      ? generateQuantitySelector(product.id)
                      : generateStandardQuantitySelector(product.id)
                  }

                  ${
                    // Show crib discount message if this is a crib collection
                    this.cribOrBedCollections.includes(collection)
                      ? `<p class='nb-body-copy-11-regular nb-bundle-collection-crib-discount' style='margin-top: 4px; display: none;'>15% off second crib</p>`
                      : ''
                  }

                  <div class="nb-bundle-collection-item-price">
                    <div class="nb-body-copy-13" js-variant-price data-price="${product.variants[0].price}">
                      $${(product.variants[0].price / 100).toFixed(2)}
                    </div>
                    <input type="hidden" value="${product.id}" name="bundle-product-${collection}">
                    <input type="hidden" name="collection" value="${collection}">
                    <button type="submit" class="nb-body-copy-12-medium nb-bundle-collection-item-button" ${
                      !product.variants.some((v) => v.available) ? 'disabled' : ''
                    }>
                      ${product.variants.some((v) => v.available) ? 'Add to bundle' : 'Sold out'}
                    </button>
                  </div>
                </div>
              </form>
            </bundle-product>
          `;
        }
      })
      .join('');
  }

  // Helper method to generate option fieldsets
  generateOptionFieldset(optionName, optionValues, variants, product, collection, uniqueId = '') {
    const isColorOption = optionName.toLowerCase().includes('color');
    const inputClass = isColorOption ? 'product-form__input--swatch' : 'product-form__input--pill';

    // Find the index of this option in the product's options array
    const optionIndex = product.options.findIndex((opt) => opt === optionName);

    // If this is a color-separated product and we're generating size options,
    // we need to only look at variants matching the current color
    const currentColor = uniqueId ? uniqueId.split('-').slice(2).join('-') : null; // Extract color from uniqueId

    // Convert variants object to array if it's not already
    const variantsArray = Array.isArray(variants) ? variants : Object.values(variants);

    // Filter variants for current color if needed
    const relevantVariants = currentColor
      ? variantsArray.filter((v) => {
          const variantColor = v.options ? v.options[0] : v.option1;
          return variantColor.toLowerCase().replace(' ', '-') === currentColor.toLowerCase().replace(' ', '');
        })
      : variantsArray;

    // Find first available value for this option among relevant variants
    const firstAvailableValue = optionValues.find((optionValue) => {
      const hasAvailableVariant = relevantVariants.some((variant) => {
        const variantOptionValue = variant.options ? variant.options[optionIndex] : variant[`option${optionIndex + 1}`];
        return variant.available && variantOptionValue === optionValue;
      });

      return hasAvailableVariant;
    });

    return `
      <fieldset class="js product-form__input ${inputClass}" js-option="${optionName}">
        ${optionValues
          .map((optionValue) => {
            const matchingVariants = relevantVariants.filter((v) => {
              const variantOptionValue = v.options ? v.options[optionIndex] : v[`option${optionIndex + 1}`];
              return variantOptionValue === optionValue;
            });

            const isAvailable = matchingVariants.some((v) => v.available);
            const matchingVariant = matchingVariants.find((v) => v.available) || matchingVariants[0];

            const optionClass = isAvailable ? '' : 'unavailable';
            const handleizedValue = handleize(optionValue);
            const isFirstAvailable = optionValue === firstAvailableValue;
            const productHandle = product.handle || `product-${product.id}`;

            // Use uniqueId in the input and label IDs
            const inputId = `${productHandle}-${optionName.toLowerCase()}-${handleizedValue}-${uniqueId}-${collection}`;

            return `
              <input 
                type="radio" 
                id="${inputId}"
                name="${productHandle}-${optionName.toLowerCase()}-${uniqueId}-${collection}"
                value="${optionValue}"
                form=""
                class="swatch-input__input"
                ${isAvailable ? '' : 'disabled'}
                ${isFirstAvailable ? 'checked' : ''}
                js-variant-option-${optionName.toLowerCase()}
                data-variant-id="${matchingVariant?.id}"
              >
              <label 
                for="${inputId}"
                title="${optionValue}"
                class="swatch-input__label ${optionClass}"
              >
                ${isColorOption ? `<span class="swatch"></span>` : `<span class="pill-text">${optionValue}</span>`}
                <div class="nb-bundle-collection-sold-out-tooltip">
                  <span>Sold out</span>
                </div>
              </label>
            `;
          })
          .join('')}
      </fieldset>
    `;
  }

  setCollectionLoading(isLoading) {
    const content = this.querySelector('[js-bundle-collection-content]');
    const products = this.querySelector('[js-bundle-collection-products]');

    if (isLoading) {
      content.classList.add('loading');
      products.innerHTML = `
        <div class="nb-bundle-collection-loading">
          <div class="nb-bundle-collection-loading-spinner"></div>
          <div class="nb-body-copy-14">Loading products...</div>
        </div>
      `;
    } else {
      content.classList.remove('loading');
    }
  }

  updateCollectionTitle(title) {
    const titleElement = this.querySelector('[js-bundle-collection-title]');
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  updateCollectionNav(activeHandle) {
    this.querySelectorAll('[js-bundle-collection-nav-item]').forEach((button) => {
      const isActive = button.dataset.collection === activeHandle;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  renderVariantOptions(product) {
    if (!product.variants || product.variants.length <= 1) return '';

    return `
      <select js-variant-selector>
        ${product.variants
          .map(
            (variant) => `
          <option value="${variant.id}" data-price="${variant.price}">
            ${variant.title} - $${(variant.price / 100).toFixed(2)}
          </option>
        `
          )
          .join('')}
      </select>
    `;
  }

  showError(message) {
    const container = this.querySelector('[js-bundle-collection-products]');
    if (container) {
      container.innerHTML = `
        <div class="nb-bundle-collection-error">
          <div class="nb-body-copy-14">${message}</div>
          <button 
            class="nb-button nb-button-secondary" 
            onclick="this.closest('bundle-board').loadCollection(this.closest('bundle-board').querySelector('[js-bundle-collection-nav-item].active').dataset.collection)"
          >
            Try Again
          </button>
        </div>
      `;
    }
  }

  parseCardsStructure() {
    try {
      const cardsDataElement = this.querySelector('[js-cards-data]');
      if (!cardsDataElement) {
        return [];
      }
      const cards = JSON.parse(cardsDataElement.textContent);
      return cards;
    } catch (error) {
      console.error('Error parsing board structure:', error);
      return [];
    }
  }

  getAges() {
    return [...new Set(this.state.cards.map((board) => board.age))];
  }

  getThemesForAge(age) {
    const themes = new Set();
    this.state.cards
      .filter((card) => card.age === age)
      .forEach((card) => {
        if (card.theme) {
          themes.add(card.theme);
        }
      });

    // Get theme data to check for hidden themes
    const themeDataElement = document.querySelector('[js-themes-data]');
    const themeData = themeDataElement ? JSON.parse(themeDataElement.textContent) : [];

    // Filter out hidden themes
    const visibleThemes = Array.from(themes).filter((theme) => {
      const themeInfo = themeData.find((t) => t.title === theme);
      return !themeInfo || !themeInfo.hidden;
    });

    return visibleThemes;
  }

  getBoard(age, theme) {
    return this.state.cards.filter((board) => board.age === age && board.theme === theme);
  }

  setState(newState) {
    const shouldRender =
      (newState.age || newState.theme) && (newState.age !== this.state.age || newState.theme !== this.state.theme);

    this.state = { ...this.state, ...newState };

    if (shouldRender) {
      this.renderBundleCards();
    }
  }

  async handleVariantAdded(event) {
    const { variantObject, collectionValue, isAutomatic } = event.detail;

    // Update the card in this.state.cards with the new variant image
    if (this.state.cards) {
      this.state.cards = this.state.cards.map((card) => {
        if (card.collection.handle === collectionValue) {
          return {
            ...card,
            variant_image: variantObject.image,
          };
        }
        return card;
      });
    }

    const cart = this.querySelector('bundle-cart');

    if (cart) {
      const success = await cart.addItem({ variantObject, collectionValue });

      // Re-render the board to update all visuals
      this.renderBundleCards();

      // Only open cart if the add was successful AND it's not an automatic addition
      if (success && !isAutomatic) {
        this.openCartAndSetFocus();
      }
    }
  }

  handleToggleChange(event) {
    if (!event?.target?.dataset) {
      console.error('Invalid event or missing dataset');
      return;
    }

    const { type, value } = event.target.dataset;
    if (!type || !value) {
      console.error('Missing type or value in dataset');
      return;
    }

    if (type === 'age') {
      const themes = this.getThemesForAge(value);
      if (!themes || themes.length === 0) {
        console.error('No themes found for age:', value);
        return;
      }

      // Get theme order data
      const themeDataElement = document.querySelector('[js-themes-data]');
      const themeData = themeDataElement ? JSON.parse(themeDataElement.textContent) : [];
      const themeOrderMap = new Map(themeData.map((theme) => [theme.title, theme.order]));

      // Sort themes and find the one with smallest order (excluding Create +)
      const sortedThemes = [...themes].sort((a, b) => {
        if (a === 'Create +') return 1;
        if (b === 'Create +') return -1;
        return (themeOrderMap.get(a) || 999) - (themeOrderMap.get(b) || 999);
      });
      const defaultTheme = sortedThemes.find((theme) => theme !== 'Create +');

      if (!defaultTheme) {
        console.error('No default theme found for age:', value);
        return;
      }

      // Update theme toggles when age changes
      const themeTogglesContainer = this.querySelector('[js-theme-toggles]');
      if (themeTogglesContainer) {
        themeTogglesContainer.innerHTML = this.renderThemeToggles(value, themes);
        this.setupListeners(); // Reattach listeners for new theme toggles
      }

      this.setState({
        age: value,
        theme: defaultTheme,
        currentBoard: this.getBoard(value, defaultTheme),
      });

      // Update URL with new age and theme
      this.updateUrlParameters(value, defaultTheme);
    } else if (type === 'theme') {
      if (!this.state.age) {
        console.error('Age not set in state');
        return;
      }

      this.setState({
        theme: value,
        currentBoard: this.getBoard(this.state.age, value),
      });

      // Update URL with new theme
      this.updateUrlParameters(this.state.age, value);
    }

    // Render the updated cards
    this.renderBundleCards();

    // First remove preselected class from all products
    this.querySelectorAll('bundle-product').forEach((product) => {
      product.classList.remove('preselected');
    });

    // Update preselected classes based on current board
    if (this.state.currentBoard) {
      this.state.currentBoard.forEach((card) => {
        if (!card?.product?.id) return;

        const product_id = card.product.id;
        const variant_id = card.variant?.id;

        if (variant_id) {
          const variants = this.querySelectorAll(
            `bundle-product[data-product-id="${product_id}"][data-variant-id="${variant_id}"]`
          );
          variants.forEach((variant) => variant.classList.add('preselected'));
        } else {
          const products = this.querySelectorAll(`bundle-product[data-product-id="${product_id}"]`);
          products.forEach((product) => product.classList.add('preselected'));
        }
      });
    }
  }

  handleCartToggle(e) {
    const cart = this.querySelector('bundle-cart');

    // Save the currently focused element before doing anything
    this.lastFocusedElement = document.activeElement;

    if (
      e.target.classList.contains('nb-bundle-cart-overlay') ||
      e.target.classList.contains('nb-bundle-close') ||
      e.target.closest('.nb-bundle-close')
    ) {
      document.body.classList.remove('cart-open');

      // Set aria-hidden when hiding the cart
      if (cart) {
        cart.setAttribute('aria-hidden', 'true');
      }

      // Return focus to the last focused element
      if (this.lastFocusedElement) {
        setTimeout(() => {
          this.lastFocusedElement.focus();
          this.lastFocusedElement = null;
        }, 100);
      }
      return;
    }

    // Open the cart
    document.body.classList.add('cart-open');

    // Remove aria-hidden when showing the cart
    if (cart) {
      cart.removeAttribute('aria-hidden');
    }

    // Set focus to the first focusable element in the cart
    if (cart) {
      setTimeout(() => {
        // Find all focusable elements in the cart
        const focusableElements = cart.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        // Focus the first element
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }, 100); // Small delay to ensure the DOM is ready
    }
  }

  renderThemeToggles(age, themes) {
    // Get theme order data from Liquid
    const themeDataElement = document.querySelector('[js-themes-data]');
    const themeData = themeDataElement ? JSON.parse(themeDataElement.textContent) : [];

    // Create a map of theme titles to their order values and hidden status
    const themeInfoMap = new Map(themeData.map((theme) => [theme.title, theme]));

    // Filter out hidden themes
    const visibleThemes = themes.filter((theme) => {
      const themeInfo = themeInfoMap.get(theme);
      return !themeInfo || !themeInfo.hidden;
    });

    // Sort themes by order value, keeping "Create +" last
    const sortedThemes = [...visibleThemes].sort((a, b) => {
      if (a === 'Create +') return 1;
      if (b === 'Create +') return -1;
      const orderA = themeInfoMap.get(a)?.order || 999;
      const orderB = themeInfoMap.get(b)?.order || 999;
      return orderA - orderB;
    });

    // Find the theme with the smallest order value (excluding "Create +")
    const defaultTheme = sortedThemes.find((theme) => theme !== 'Create +');

    return sortedThemes
      .map((theme) => {
        const themeHandle = handleize(theme);
        return `
          <div class="nb-theme-toggle" data-theme="${theme}">
            <input
              type="radio"
              name="theme-${age}"
              data-type="theme"
              data-value="${theme}"
              id="theme-${themeHandle}"
              value="${theme}"
              class="nb-theme"
              js-toggle
              ${theme === defaultTheme ? 'checked' : ''}
            >
            <label for="theme-${themeHandle}" class="nb-label nb-body-copy-15 nb-theme-label">
              ${theme}
            </label>
          </div>
      `;
      })
      .join('');
  }

  renderToggles() {
    // Render age toggles
    const ageTogglesContainer = this.querySelector('[js-age-toggles]');
    if (ageTogglesContainer) {
      const ages = this.getAges();
      const ageTogglesHTML = ages
        .map(
          (age, index) => `
        <div>
          <input
            type="radio"
            name="age"
            data-type="age"
            id="board-${handleize(age)}"
            value="${age}"
            data-value="${age}"
            class="nb-radio"
            js-toggle
            ${index === 0 ? 'checked' : ''}
          >
          <label for="board-${handleize(age)}" class="nb-label nb-body-copy-24">
            ${age}
          </label>
        </div>
      `
        )
        .join('');

      ageTogglesContainer.innerHTML = ageTogglesHTML;
    }

    // Render theme toggles
    const themeTogglesContainer = this.querySelector('[js-theme-toggles]');
    if (themeTogglesContainer) {
      const selectedAge = this.state.age || this.getAges()[0];
      const themes = this.getThemesForAge(selectedAge);
      themeTogglesContainer.innerHTML = this.renderThemeToggles(selectedAge, themes);
    }

    // Re-attach event listeners
    this.setupListeners();
  }

  renderBundleCards() {
    const bundleCardsContainer = this.querySelector('[js-bundle-cards]');
    if (!bundleCardsContainer) return;

    const filteredCards = this.state.cards.filter(
      (card) => card.age === this.state.age && card.theme === this.state.theme
    );

    // Create cards HTML
    const cardsHTML = this.generateCardsHTML(filteredCards);

    // Set the HTML content
    bundleCardsContainer.innerHTML = cardsHTML;

    // Re-initialize collection toggles after rendering new cards
    this.initializeBundleCollectionToggles();
  }

  // Helper method to generate cards HTML (extracted from renderBundleCards)
  generateCardsHTML(filteredCards) {
    return filteredCards
      .map((card) => {
        const collection = card.collection.handle;
        const cart = document.querySelector('bundle-cart');
        const showTag = cart && card.collection.eligible_for_discounts;

        // Get all items from this collection in the cart
        const collectionItems =
          cart && cart.items
            ? Array.from(cart.items.values()).filter((item) => item.collectionValue === collection)
            : [];

        // Use the first item's image if available, otherwise use the card's default image
        const displayImage = collectionItems.length > 0 ? collectionItems[0].variantObject.image : card.variant_image;

        return `
          <button
              type="button"
              class="bundle-card" 
              aria-label="Explore ${card.collection.title}"
              data-age="${card.age}" 
              data-theme="${card.theme}" 
              js-bundle-collection-toggle 
              data-collection="${collection}" 
              data-position="${card.position}">
            ${showTag ? this.generateTagHTML() : ''}
            
            ${
              collectionItems.length > 0
                ? `
              <div class="nb-bundle-checkbox is-visible" js-bundle-checkbox data-collection="${collection}" data-size="large">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="12" fill="#173482"/>
                  <path d="M17.5 7.875L9.9375 15.4375L6.5 12" stroke="#F9F5F2" stroke-width="1.54688" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            `
                : ''
            }

            <div class="bundle-card-text">
              <span class="nb-body-copy-15-medium">Explore ${card.collection.title}</span>
            </div>

            <div class="bundle-card-image">
              <img src="${displayImage}" 
                alt="${card.collection.title}" 
                js-bundle-card-image 
                data-collection="${collection}">
            </div>
            
            <div class="bundle-card-title">
                <span class="nb-body-copy-13-medium">${card.collection.title}</span>
            </div>
            <div class="bundle-card-overlay"></div>
          </button>
        `;
      })
      .join('');
  }

  // Helper method for tag HTML
  generateTagHTML() {
    return `
      <div class="bundle-card-tag nb-body-copy-11-medium js-bundle-discount-tag">
        <span class="bundle-card-tag-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" preserveAspectRatio="xMidYMid meet">
            <path d="m13.73 8.94-4.78 4.78a1.334 1.334 0 0 1-1.887 0L1.336 8V1.333h6.667l5.726 5.727a1.333 1.333 0 0 1 0 1.88Z" stroke="#0A0A0A" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M4.668 4.667h.006" stroke="#0A0A0A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </span>
        <span class="bundle-card-tag-text">
          <span>Save 15%</span>
        </span>
      </div>
    `;
  }

  // Make sure this function exists to handle the toggle functionality
  initializeBundleCollectionToggles() {
    const collectionToggles = this.querySelectorAll('[js-bundle-collection-toggle]');
    const overlay = this.querySelector('[js-bundle-collection-overlay]');
    const closeButton = this.querySelector('[js-bundle-collection-close]');

    // Bind collection toggle clicks
    collectionToggles.forEach((toggle) => {
      toggle.addEventListener('click', this.toggleCollectionSidebar.bind(this));
    });

    // Bind overlay click
    if (overlay) {
      overlay.addEventListener('click', this.toggleCollectionSidebar.bind(this));
    }

    // Bind close button click
    if (closeButton) {
      closeButton.addEventListener('click', this.toggleCollectionSidebar.bind(this));
    }
  }

  updateCheckboxVisibility() {
    const cart = this.querySelector('bundle-cart');
    if (!cart) return;

    const items = Array.from(cart.items.values());

    // Update checkboxes in bundle cards
    this.querySelectorAll('[js-bundle-checkbox][data-size="large"]').forEach((checkbox) => {
      const collection = checkbox.dataset.collection;
      const hasItemsInCollection = items.some((item) => item.collectionValue === collection);
      checkbox.classList.toggle('is-visible', hasItemsInCollection);
    });

    // Update checkboxes in product cards
    this.querySelectorAll('bundle-product').forEach((product) => {
      const checkbox = product.querySelector('[js-bundle-checkbox]');
      if (!checkbox) return;

      const variantId = product.dataset.variantId;
      const productId = product.dataset.productId;
      const currentCollection = checkbox.dataset.collection;

      const isInCart = items.some((item) => {
        if (variantId) {
          // Check for exact variant match in the same collection
          return item.variantObject.id === variantId && item.collectionValue === currentCollection;
        }
        // Check for product match in the same collection
        return item.variantObject.product_id === productId && item.collectionValue === currentCollection;
      });

      checkbox.classList.toggle('is-visible', isInCart);
    });
  }

  calculateDiscount(item, index, eligibleItemCount) {
    const { collectionValue } = item;

    // Check if collection is eligible for discount
    const isEligibleForDiscount = this.discountCollections.some((col) => col.handle === collectionValue);

    if (!isEligibleForDiscount) {
      return 0;
    }

    // Base discount on total items in cart
    const totalItems = this.items.size;

    // For 2 total items in cart, eligible items get 10%
    if (totalItems === 2) {
      return 0.1;
    }

    // For 3+ total items in cart, eligible items get 15%
    if (totalItems >= 3) {
      return 0.15;
    }

    return 0;
  }

  async calculateTotals() {
    // Sort items by price descending only
    const sortedItems = Array.from(this.items.values()).sort((a, b) => {
      return parseFloat(b.variantObject.price) - parseFloat(a.variantObject.price);
    });

    // Count eligible items
    const eligibleItems = sortedItems.filter((item) =>
      this.discountCollections.some((col) => col.handle === item.collectionValue)
    );

    let subtotal = 0;
    let savings = 0;
    let itemDetails = [];

    sortedItems.forEach((item, index) => {
      const quantity = parseInt(item.variantObject.quantity || '1');
      const unitPrice = Math.round(parseFloat(item.variantObject.price) * 100); // Convert to cents
      const totalPrice = unitPrice * quantity;

      subtotal += totalPrice;

      const discount = this.calculateDiscount(item, index, eligibleItems.length);
      const itemSavings = Math.round(totalPrice * discount);
      savings += itemSavings;

      itemDetails.push({
        title: item.variantObject.product_title,
        originalPrice: (totalPrice / 100).toFixed(2),
        discountPercent: (discount * 100).toFixed(0),
        savings: (itemSavings / 100).toFixed(2),
        finalPrice: ((totalPrice - itemSavings) / 100).toFixed(2),
        quantity,
      });
    });

    const finalSubtotal = Math.round(subtotal);
    const finalSavings = Math.round(savings);
    const finalTotal = finalSubtotal - finalSavings;

    const savingsElements = document.querySelectorAll('[js-bundle-savings]');
    savingsElements.forEach((element) => {
      element.textContent = `Bundle Savings: $${(finalSavings / 100).toFixed(2)}`;
      element.dataset.savings = finalSavings;
    });

    return {
      subtotal: finalSubtotal,
      savings: finalSavings,
      total: finalTotal,
      sortedItems,
    };
  }

  // Add this method to help track when the bundle is updated
  onBundleUpdate() {
    this.updateTotals();
  }

  // Add method to update URL parameters without reloading the page
  updateUrlParameters(age, theme) {
    if (!age) return;

    const url = new URL(window.location.href);

    // Set or update parameters
    url.searchParams.set('age', encodeURIComponent(age));

    if (theme) {
      // Use "design" instead of "theme"
      url.searchParams.set('design', encodeURIComponent(theme));
    } else {
      url.searchParams.delete('design');
    }

    // Update URL without reloading the page
    window.history.replaceState({}, '', url.toString());
  }

  // Add a new method to open the cart and set focus
  openCartAndSetFocus() {
    // Save the currently focused element
    this.lastFocusedElement = document.activeElement;

    // Open the cart
    document.body.classList.add('cart-open');

    // Set focus to the first focusable element in the bundle cart
    setTimeout(() => {
      const bundleCart = document.querySelector('#bundle-cart');
      if (bundleCart) {
        const firstFocusable = bundleCart.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          bundleCart.setAttribute('tabindex', '-1');
          bundleCart.focus();
        }
      }
    }, 100);
  }
}

class BundleProduct extends HTMLElement {
  constructor() {
    super();
    this.initializeElements();
    this.initializeEventListeners();
  }

  initializeElements() {
    this.form = this.querySelector('form');
    this.select = this.querySelector('select');
    this.options = this.querySelectorAll('input[type="radio"]');
    this.image = this.querySelector('[js-image] img');
    this.variantPrice = this.querySelector('[js-variant-price]');
    this.discountBadge = this.querySelector('[js-bundle-discount]');
    this.collection = this.querySelector('input[name="collection"]')?.value;
    this.checkbox = this.querySelector('[js-bundle-checkbox]');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.quantityPacks = this.querySelectorAll('[js-quantity-pack]');
    this.quantityInput = this.querySelector('[js-quantity-input]');
    this.quantityDecrease = this.querySelector('[js-quantity-decrease]');
    this.quantityIncrease = this.querySelector('[js-quantity-increase]');
    this.displayCard = this.dataset.displayCard;

    // Initialize checkbox visibility
    if (this.checkbox) {
      const cart = document.querySelector('bundle-cart');
      if (cart) {
        const items = Array.from(cart.items.values());
        const variantId = this.dataset.variantId;
        const productId = this.dataset.productId;
        const isInCart = items.some(
          (item) =>
            item.collectionValue === this.collection &&
            (item.variantObject.id === variantId || item.variantObject.product_id === productId)
        );
        this.checkbox.classList.toggle('is-visible', isInCart);
      }
    }
  }

  initializeEventListeners() {
    if (this.displayCard === 'Break out variants into separate cards') {
      // For separate cards, we don't need option change handlers
      this.setupFormHandler();
      this.setupQuantityHandlers();
    } else {
      // For combined cards, use the full option handling
      this.setupOptionHandlers();
      this.setupFormHandler();
      this.setupQuantityHandlers();
    }
  }

  setupOptionHandlers() {
    // Only set up option handlers if we're not using separate cards
    if (this.displayCard !== 'Break out variants into separate cards') {
      this.options.forEach((option) => {
        option.addEventListener('change', () => this.handleOptionChange(option));
      });
      // Initialize with first checked options
      this.updateSelectedVariant();
    }
  }

  handleOptionChange(changedOption) {
    if (this.displayCard === 'Break out variants into separate cards') {
      // For separate cards, just update the variant ID
      const variantId = changedOption.dataset.variantId;
      if (variantId) {
        this.updateVariantUI(variantId);
      }
    } else if (this.displayCard === 'Break out variants into separate colors') {
      // For separate colors, update the variant based on the selected option
      const variantId = changedOption.dataset.variantId;
      if (variantId) {
        this.updateVariantUI(variantId);
      }
    } else {
      // For default case (null or not set) and combined cards, use the full variant selection logic
      const variantId = changedOption.dataset.variantId;
      if (variantId) {
        this.updateVariantUI(variantId);
      }
    }
  }

  updateVariantUI(variantId) {
    // Find the matching option in the select element
    const option = this.select?.querySelector(`option[value="${variantId}"]`);
    if (!option) return;

    // Update the select value
    this.select.value = variantId;

    // Update the image if one is specified
    const image = option.dataset.image;
    if (image && this.image) {
      this.image.src = image + '&width=300';
      this.image.alt = option.title;
    }

    // Update the price
    const price = option.dataset.price;
    if (price && this.variantPrice) {
      this.variantPrice.dataset.price = price;
      this.variantPrice.textContent = `$${(parseInt(price) / 100).toFixed(2)}`;
      this.updatePriceWithQuantity();
    }

    // Update the title if needed
    const titleElement = this.querySelector('.nb-bundle-collection-item-title h3');
    if (titleElement && option.title) {
      const productTitle = this.querySelector('input[name^="bundle-item-product-title"]')?.value || '';
      const variantTitle = option.title.split(' - ')[0]; // Get just the variant part
      titleElement.textContent = `${productTitle} in ${variantTitle}`;
    }
  }

  setupFormHandler() {
    this.form?.addEventListener('submit', (event) => this.handleFormSubmit(event));
  }

  // Add new method to handle quantity changes
  setupQuantityHandlers() {
    // Handle curtain pack quantity changes
    this.quantityPacks?.forEach((pack) => {
      pack.addEventListener('change', () => this.updatePriceWithQuantity());
    });

    // Handle standard quantity input changes
    if (this.quantityInput) {
      this.quantityInput.addEventListener('input', () => this.updatePriceWithQuantity());

      // Handle decrease button
      if (this.quantityDecrease) {
        this.quantityDecrease.addEventListener('click', () => {
          const currentValue = parseInt(this.quantityInput.value) || 1;
          if (currentValue > 1) {
            this.quantityInput.value = currentValue - 1;
            this.updatePriceWithQuantity();
          }
        });
      }

      // Handle increase button
      if (this.quantityIncrease) {
        this.quantityIncrease.addEventListener('click', () => {
          const currentValue = parseInt(this.quantityInput.value) || 1;
          this.quantityInput.value = currentValue + 1;
          this.updatePriceWithQuantity();
        });
      }
    }
  }

  // Add this new method
  updatePriceWithQuantity() {
    if (!this.variantPrice) return;

    const basePrice = parseInt(this.variantPrice.dataset.price) / 100;
    let quantity = '1';

    // Check if we have curtain packs or standard quantity input
    if (this.quantityPacks?.length > 0) {
      // For curtains with pack options
      quantity = Array.from(this.quantityPacks).find((pack) => pack.checked)?.value || '1';
    } else if (this.quantityInput) {
      // For standard quantity input
      quantity = this.quantityInput.value || '1';
    }

    const quantityNum = parseInt(quantity);
    let totalPrice = basePrice * quantityNum;

    // Check if this is a crib and apply discount if quantity >= 2
    const bundleBoard = this.closest('bundle-board');
    const isCrib =
      bundleBoard && bundleBoard.cribOrBedCollections && bundleBoard.cribOrBedCollections.includes(this.collection);

    if (isCrib && quantityNum >= 2) {
      // Apply discount: first crib at full price, second crib at 15% off, rest at full price
      const firstCribPrice = basePrice;
      const secondCribPrice = basePrice * 0.85; // 15% off second crib only
      const remainingCribsPrice = basePrice * Math.max(0, quantityNum - 2); // 3rd+ cribs at full price
      totalPrice = firstCribPrice + secondCribPrice + remainingCribsPrice;
    }

    this.variantPrice.textContent = `$${totalPrice.toFixed(2)}`;

    // Show/hide crib discount message based on quantity
    const cribDiscountMessage = this.querySelector('.nb-bundle-collection-crib-discount');
    if (cribDiscountMessage) {
      cribDiscountMessage.style.display = isCrib && quantityNum >= 2 ? 'block' : 'none';
    }
  }

  async handleFormSubmit(event) {
    event.preventDefault();
    const formData = new FormData(this.form);
    const formObject = Object.fromEntries(formData.entries());

    const variantId = this.getSelectedVariantId(formObject);
    if (!variantId) return;

    const variantObject = this.buildVariantObject(formObject, variantId);

    try {
      // Create a custom event with a detail property
      const variantAddedEvent = new CustomEvent('variantAdded', {
        bubbles: true,
        detail: {
          variantObject,
          collectionValue: this.collection,
        },
      });

      // Dispatch the event
      this.dispatchEvent(variantAddedEvent);

      // Close the collection sidebar
      const bundleBoard = this.closest('bundle-board');
      if (bundleBoard) {
        bundleBoard.closeSidebar();
        await bundleBoard.renderBundleCards();
      }
    } catch (error) {
      console.error('Error adding variant:', error);
    }
  }

  // UI Updates
  updateVariantUI(variantId) {
    const option = this.select?.querySelector(`option[value="${variantId}"]`);
    if (!option) return;

    this.updateImage(option.dataset.image);
    this.updatePrice(option.dataset.price);
    this.updateSelectValue(variantId);
  }

  updateImage(imageUrl) {
    if (imageUrl && this.image) {
      this.image.src = imageUrl + '&width=300';
    }
  }

  updatePrice(price) {
    if (price && this.variantPrice) {
      this.variantPrice.dataset.price = price;
      this.updatePriceWithQuantity(); // Replace direct price update with quantity-aware update
    }
  }

  updateSelectValue(variantId) {
    if (this.select) {
      this.select.value = variantId;
    }
  }

  // Helper Methods
  isOptionAvailable(option) {
    const variantId = option.dataset.variantId;
    const optionEl = this.select?.querySelector(`option[value="${variantId}"]`);
    return optionEl && !optionEl.disabled;
  }

  updateOptionState(option, isAvailable) {
    option.disabled = !isAvailable;
    option.parentElement.classList.toggle('unavailable', !isAvailable);
  }

  updateSelectedVariant() {
    // Get all checked options
    const checkedOptions = Array.from(this.options)
      .filter((option) => option.checked)
      .map((option) => option.value);

    // Find the matching variant in the select element
    const matchingOption = Array.from(this.select?.options || []).find((option) => {
      const variantTitle = option.title;
      return checkedOptions.every((value) => variantTitle.includes(value));
    });

    if (matchingOption) {
      // Update the select value
      this.select.value = matchingOption.value;

      // Update the image if one is specified
      const image = matchingOption.dataset.image;
      if (image && this.image) {
        this.image.src = image + '&width=300';
      }

      // Update the price
      const price = matchingOption.dataset.price;
      if (price && this.variantPrice) {
        this.variantPrice.dataset.price = price;
        this.updatePriceWithQuantity();
      }
    }
  }

  isItemInCart(cart, variantId, productId) {
    const items = Array.from(cart.items.values());
    const currentCollection = this.collection;

    if (variantId) {
      // First check for exact variant match in the same collection
      const variantMatch = items.some(
        (item) => variantId && item.variantObject.id === variantId && item.collectionValue === currentCollection
      );

      return variantMatch;
    }

    // If no variant match, check for product match in the same collection
    return items.some(
      (item) => item.variantObject.product_id === productId && item.collectionValue === currentCollection
    );
  }

  getSelectedVariantId(formObject) {
    const variantKey = Object.keys(formObject).find((k) => k.startsWith('bundle-variant-'));
    return formObject[variantKey];
  }

  buildVariantObject(formObject, variantId) {
    const collection = formObject.collection;

    // Get quantity from pack or standard input
    let quantity = '1';
    if (this.quantityPacks?.length > 0) {
      // For curtains with pack options
      quantity = Array.from(this.quantityPacks).find((pack) => pack.checked)?.value || '1';
    } else if (this.quantityInput) {
      // For standard quantity input
      quantity = this.quantityInput.value || '1';
    }

    return {
      id: variantId,
      image: formObject[`bundle-item-image-${variantId}-${collection}`],
      title: formObject[`bundle-item-variant-title-${variantId}-${collection}`],
      price: formObject[`bundle-item-variant-price-${variantId}-${collection}`],
      product_title: formObject[`bundle-item-product-title-${variantId}-${collection}`],
      product_id: formObject[`bundle-product-${collection}`],
      display_card: formObject['bundle-display-card'],
      quantity,
      shipping: formObject[`bundle-item-variant-shipping-${variantId}-${collection}`],
      eta: formObject[`bundle-item-variant-eta-${variantId}-${collection}`],
    };
  }

  dispatchVariantAddedEvent(variantObject) {
    this.dispatchEvent(
      new CustomEvent('variantAdded', {
        bubbles: true,
        detail: {
          variantObject,
          collectionValue: this.collection,
        },
      })
    );
  }
}

class BundleCart extends HTMLElement {
  constructor() {
    super();
    this.items = new Map();
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.collectionData = parseCollectionData();

    // Add collections that get discounts
    this.discountCollections = this.collectionData.filter((collection) => collection.eligible_for_discounts);

    // Get crib/bed collections
    const cribOrBedDataElement = document.querySelector('[js-crib-or-bed-data]');
    this.cribOrBedCollections = cribOrBedDataElement ? JSON.parse(cribOrBedDataElement.textContent) : [];

    // Set initial aria-hidden state
    this.setAttribute('aria-hidden', 'true');

    // Add mutation observer to sync aria-hidden with cart visibility
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isCartOpen = document.body.classList.contains('cart-open');
          this.setAttribute('aria-hidden', (!isCartOpen).toString());
        }
      });
    });

    // Start observing the body element for class changes
    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  disconnectedCallback() {
    // Clean up the observer when the element is removed
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  async dispatchCartUpdatedEvent(totals) {
    this.dispatchEvent(
      new CustomEvent('cartUpdated', {
        bubbles: true,
        detail: {
          items: Array.from(this.items.values()),
          totals,
        },
      })
    );
  }

  updateBundleCount() {
    const bundleCountElements = document.querySelectorAll('[js-bundle-count]');
    bundleCountElements.forEach((element) => {
      element.textContent = this.items.size;
    });
  }

  calculateDiscount(item, index, eligibleItemCount) {
    const { collectionValue } = item;

    // Check if this item is a crib/bed using the existing cribOrBedCollections data
    const isCribOrBed = this.cribOrBedCollections.includes(collectionValue);

    // If this is a crib/bed, check for special crib discount logic
    if (isCribOrBed) {
      // Get all items from the cart
      const allItems = Array.from(this.items.values());

      // Get all crib items and sort by price (highest to lowest)
      const allCribItems = allItems
        .filter((cartItem) => this.cribOrBedCollections.includes(cartItem.collectionValue))
        .sort((a, b) => parseFloat(b.variantObject.price) - parseFloat(a.variantObject.price));

      // Count total cribs across all crib items (including quantities)
      const totalCribCount = allCribItems.reduce((count, cribItem) => {
        return count + parseInt(cribItem.variantObject.quantity || '1');
      }, 0);

      // Only apply discount if there are 2+ total cribs
      if (totalCribCount >= 2) {
        // Find position of this specific item in the sorted crib list
        const cribItemIndex = allCribItems.findIndex(
          (cribItem) =>
            cribItem.variantObject.id === item.variantObject.id && cribItem.collectionValue === item.collectionValue
        );

        const quantity = parseInt(item.variantObject.quantity || '1');

        // Calculate how many cribs come before this item
        let cribsBefore = 0;
        for (let i = 0; i < cribItemIndex; i++) {
          cribsBefore += parseInt(allCribItems[i].variantObject.quantity || '1');
        }

        // Determine how many of this item's cribs get discount
        let discountedCribs = 0;
        if (cribsBefore === 0 && quantity >= 2) {
          // This is the first/highest-priced crib item with quantity >= 2
          // Only the second crib gets discount (position 1)
          discountedCribs = 1;
        } else if (cribsBefore === 1) {
          // This crib item starts at position 1 (second crib position)
          // First crib of this item gets discount
          discountedCribs = 1;
        } else if (cribsBefore >= 2) {
          // This crib item starts at position 2+, no discount
          discountedCribs = 0;
        }

        // Calculate blended discount rate
        if (discountedCribs > 0) {
          return (discountedCribs * 0.15) / quantity;
        }
      }

      // No discount if only one total crib or this item doesn't qualify
      return 0;
    }

    // Check if collection is eligible for discount (non-crib items)
    const isEligibleForDiscount = this.discountCollections.some((col) => col.handle === collectionValue);

    if (!isEligibleForDiscount) {
      return 0;
    }

    // Base discount on total items in cart for non-crib items
    const totalItems = this.items.size;

    // For 2 total items in cart, eligible items get 15%
    if (totalItems === 2) {
      return 0.1;
    }

    // For 3+ total items in cart, eligible items get 15%
    if (totalItems >= 3) {
      return 0.15;
    }

    return 0;
  }

  async calculateTotals() {
    // Sort items by price descending only
    const sortedItems = Array.from(this.items.values()).sort((a, b) => {
      return parseFloat(b.variantObject.price) - parseFloat(a.variantObject.price);
    });

    // Count eligible items
    const eligibleItems = sortedItems.filter((item) =>
      this.discountCollections.some((col) => col.handle === item.collectionValue)
    );

    let subtotal = 0;
    let savings = 0;
    let itemDetails = [];

    sortedItems.forEach((item, index) => {
      const quantity = parseInt(item.variantObject.quantity || '1');
      const unitPrice = Math.round(parseFloat(item.variantObject.price) * 100); // Convert to cents
      const totalPrice = unitPrice * quantity;

      subtotal += totalPrice;

      const discount = this.calculateDiscount(item, index, eligibleItems.length);
      const itemSavings = Math.round(totalPrice * discount);
      savings += itemSavings;

      itemDetails.push({
        title: item.variantObject.product_title,
        originalPrice: (totalPrice / 100).toFixed(2),
        discountPercent: (discount * 100).toFixed(0),
        savings: (itemSavings / 100).toFixed(2),
        finalPrice: ((totalPrice - itemSavings) / 100).toFixed(2),
        quantity,
      });
    });

    const finalSubtotal = Math.round(subtotal);
    const finalSavings = Math.round(savings);
    const finalTotal = finalSubtotal - finalSavings;

    const savingsElements = document.querySelectorAll('[js-bundle-savings]');
    savingsElements.forEach((element) => {
      element.textContent = `Bundle Savings: $${(finalSavings / 100).toFixed(2)}`;
      element.dataset.savings = finalSavings;
    });

    return {
      subtotal: finalSubtotal,
      savings: finalSavings,
      total: finalTotal,
      sortedItems,
    };
  }

  getSortedItems() {
    return Array.from(this.items.values()).sort((a, b) => {
      return parseFloat(b.variantObject.price) - parseFloat(a.variantObject.price);
    });
  }

  async render() {
    const totals = await this.calculateTotals();
    await this.dispatchCartUpdatedEvent(totals);

    // Render empty state if no items
    if (this.items.size === 0) {
      this.innerHTML = '<div class="nb-bundle-cart-empty nb-h4">Nothing here yet!</div>';
      return;
    }

    // Use getSortedItems() for rendering
    const sortedItems = this.getSortedItems();
    this.innerHTML = this.generateCartHTML({ ...totals, sortedItems });
    this.attachEventListeners();
    this.updateBundleCount();
  }

  generateCartHTML(totals) {
    const items = totals.sortedItems
      .map(({ variantObject, collectionValue }, index) => {
        const image = variantObject.image;
        const quantity = parseInt(variantObject.quantity || '1');
        const unitPrice = parseFloat(variantObject.price) * 100; // Convert to cents
        const totalPrice = unitPrice * quantity;

        // Calculate discount
        const discount = this.calculateDiscount(
          { variantObject, collectionValue },
          index,
          totals.sortedItems.filter((item) =>
            this.discountCollections.find((col) => col.handle === item.collectionValue)
          ).length
        );

        const discountedPrice = totalPrice * (1 - discount);

        return `
        <div class="nb-bundle-cart-item">
          <div class="nb-bundle-cart-item-image">
            ${image ? `<img src="${image}" alt="${variantObject.title}">` : ''}

            <div class="nb-bundle-cart-item-badge nb-body-copy-11-medium">
              <span js-bundle-cart-discount data-collection="${collectionValue}">${
          // Don't show badge for cribs, they have their own "15% off second crib" message
          discount > 0 && !this.cribOrBedCollections.includes(collectionValue)
            ? `${(discount * 100).toFixed(0)}% off`
            : ''
        }</span>
            </div>
          </div>
          <div class="nb-bundle-cart-item-details">
            <h3 class='nb-body-copy-13-medium'>${variantObject.product_title}</h3>
            <p class='nb-body-copy-12 nb-bundle-cart-item-details-variant'>
              ${variantObject.title !== 'Default Title' ? variantObject.title : ''}
              ${quantity > 1 ? ` × ${quantity}` : ''}
            </p>
            ${
              // Show crib discount message if this crib item gets a discount
              (() => {
                if (!this.cribOrBedCollections.includes(collectionValue)) return '';

                // Get all crib items and sort by price
                const allItems = Array.from(this.items.values());
                const allCribItems = allItems
                  .filter((cartItem) => this.cribOrBedCollections.includes(cartItem.collectionValue))
                  .sort((a, b) => parseFloat(b.variantObject.price) - parseFloat(a.variantObject.price));

                const totalCribCount = allCribItems.reduce((count, cribItem) => {
                  return count + parseInt(cribItem.variantObject.quantity || '1');
                }, 0);

                if (totalCribCount >= 2) {
                  const cribItemIndex = allCribItems.findIndex(
                    (cribItem) =>
                      cribItem.variantObject.id === variantObject.id && cribItem.collectionValue === collectionValue
                  );

                  let cribsBefore = 0;
                  for (let i = 0; i < cribItemIndex; i++) {
                    cribsBefore += parseInt(allCribItems[i].variantObject.quantity || '1');
                  }

                  // Show message if this item qualifies for discount
                  if ((cribsBefore === 0 && quantity >= 2) || cribsBefore === 1) {
                    return `<p class='nb-body-copy-11 nb-bundle-cart-discount-text nb-body-copy-11-regular' style='margin-top: 1rem; margin-bottom: 1rem;'>15% off second crib</p>`;
                  }
                }

                return '';
              })()
            }
            <div class="nb-bundle-cart-item-details-eta-shipping">
              <div>
                ${variantObject.eta ? `<span class="nb-body-copy-12">ETA: ${variantObject.eta}</span>` : ''}
              </div>
              <div>
                ${
                  variantObject.shipping
                    ? `<span class="nb-body-copy-12">Shipping: ${variantObject.shipping}</span>`
                    : ''
                }
              </div>
            </div>
            <div class="nb-bundle-cart-item-price">
              <div class='nb-body-copy-13'>
                ${
                  discount > 0
                    ? `<span class="nb-bundle-cart-item-price-original">$${(totalPrice / 100).toFixed(2)}</span>
                   <span class="nb-bundle-cart-item-price-discounted">$${(discountedPrice / 100).toFixed(2)}</span>`
                    : `<span class="nb-bundle-cart-item-price">$${(totalPrice / 100).toFixed(2)}</span>`
                }
              </div>
              <button type="button" 
                class="nb-bundle-cart-item-remove nb-body-copy-12-medium" 
                data-variant-id="${variantObject.id}-${collectionValue}">
                Remove
              </button>
            </div>
          </div>
        </div>
      `;
      })
      .join('');

    // Check if bundle contains a crib or bed
    const hasCribOrBed = totals.sortedItems.some((item) => {
      return this.cribOrBedCollections.includes(item.collectionValue);
    });

    const cartSummary = `
      <div class="nb-bundle-cart-summary">
        <div class="nb-bundle-cart-subtotal">
          <span class="nb-body-copy-13">Subtotal</span>
          <span class="nb-body-copy-13">$${(totals.subtotal / 100).toFixed(2)}</span>
        </div>
        <div class="nb-bundle-cart-savings">
          <span class="nb-body-copy-13">Bundle savings</span>
          <span class="nb-body-copy-13">- $${(totals.savings / 100).toFixed(2)}</span>
        </div>
        <div class="nb-bundle-cart-total">
          <span class="nb-body-copy-13-medium">Bundle total</span>
          <span class="nb-body-copy-13-medium">$${(totals.total / 100).toFixed(2)}</span>
        </div>
        ${
          !hasCribOrBed
            ? `
          <div class="nb-bundle-cart-error nb-body-copy-12">
            Bundle must contain a crib or bed
          </div>
        `
            : ''
        }
        <button type="button" 
          class="nb-button nb-button-primary nb-bundle-cart-checkout" 
          js-bundle-checkout
          ${!hasCribOrBed ? 'disabled' : ''}>
          Add bundle to cart
        </button>
      </div>
    `;

    return `<div class="nb-bundle-cart-items">${items}</div>${cartSummary}`;
  }

  attachEventListeners() {
    // Attach remove button listeners
    this.querySelectorAll('.nb-bundle-cart-item-remove').forEach((button) => {
      button.addEventListener('click', () => {
        this.items.delete(button.dataset.variantId);
        this.render();

        // Get the bundle board and re-render its cards
        const bundleBoard = this.closest('bundle-board');
        if (bundleBoard) {
          bundleBoard.renderBundleCards();
        }
      });
    });

    // Attach checkout button listener
    const checkoutButton = this.querySelector('[js-bundle-checkout]');
    if (checkoutButton) {
      checkoutButton.addEventListener('click', () => this.handleCheckout());
    }
  }

  async handleCheckout() {
    try {
      const items = Array.from(this.items.values());

      if (!items.length) {
        throw new Error('No items to add to cart');
      }

      // Validate all variant IDs exist before submitting
      const invalidVariants = await this.validateVariants();
      if (invalidVariants.length > 0) {
        const invalidItems = items.filter((item) => invalidVariants.includes(item.variantObject.id));
        const invalidNames = invalidItems.map((item) => item.variantObject.product_title).join(', ');
        alert(`Some items are no longer available: ${invalidNames}. Please remove them and try again.`);
        return;
      }

      // Sort items to ensure consistent discount calculation
      const sortedItems = this.getSortedItems();

      // Count eligible items
      const eligibleItemCount = sortedItems.filter((item) =>
        this.discountCollections?.some((col) => col.handle === item.collectionValue)
      ).length;

      // Build components array first so we can use it for both properties
      const componentsArray = sortedItems.map((item, index) => {
        // Calculate proper discount for each item
        const discount = this.calculateDiscount(item, index, eligibleItemCount);
        const originalPrice = parseFloat(item.variantObject.price);
        const discountedPrice = originalPrice * (1 - discount);

        const savings = originalPrice - discountedPrice;
        const attributes = {
          'Retail Price': '$' + originalPrice.toFixed(2),
        };

        // Only add Savings attribute if there's a discount
        if (savings > 0) {
          attributes.Savings = '$' + savings.toFixed(2);
        }

        attributes.Shipping = item.variantObject.shipping || 'Standard';
        attributes.ETA = item.variantObject.eta || '';

        return {
          id: parseInt(item.variantObject.id),
          price: discountedPrice,
          quantity: parseInt(item.variantObject.quantity || '1'),
          attributes: attributes,
          image: item.variantObject.image,
          product_title: item.variantObject.product_title,
        };
      });

      let formData = {
        items: [
          {
            id: 51524946067624,
            quantity: 1,
            properties: {
              _components: JSON.stringify(componentsArray),
              _components_for_rebuy: componentsArray,
              'Retail Price':
                '$' +
                sortedItems
                  .reduce((acc, { variantObject }) => {
                    const price = parseFloat(variantObject.price);
                    return acc + price;
                  }, 0)
                  .toFixed(2),
              'Total Savings':
                '$' +
                sortedItems
                  .reduce((acc, { variantObject }, index) => {
                    const price = parseFloat(variantObject.price);
                    const discount = this.calculateDiscount(
                      { variantObject, collectionValue: sortedItems[index].collectionValue },
                      index,
                      eligibleItemCount
                    );
                    const discountAmount = price * discount;
                    return acc + discountAmount;
                  }, 0)
                  .toFixed(2),
              ...sortedItems.reduce(
                (acc, item, index) => ({
                  ...acc,
                  [`_item_${index + 1}`]: item.variantObject.product_title + ' - ' + item.variantObject.title,
                  [`_eta_${index + 1}`]: item.variantObject.eta || '',
                  [`_shipping_${index + 1}`]: item.variantObject.shipping || 'Standard',
                }),
                {}
              ),
            },
          },
        ],
      };

      // Get sections to render from the cart drawer
      if (this.cart) {
        formData.sections = 'cart-drawer,cart-icon-bubble';
      }

      const bundleBoard = this.closest('bundle-board');
      if (bundleBoard) {
        bundleBoard.setState({
          isCollectionOpen: false,
          activeCollection: null,
        });
      }

      console.log('========================================');
      console.log('formData');
      console.log(formData);
      console.log('========================================');

      fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      })
        .then((response) => response.json())
        .then((response) => {
          // this.cart.renderContents(response);
        })
        .catch((e) => {
          console.error('Fetch error:', e);
        })
        .finally(() => {
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          // Close the bundle cart before sending the request
          document.body.classList.remove('cart-open');
        });
    } catch (error) {
      console.error('Error in handleCheckout:', error);
      throw error;
    }
  }

  async addItem({ variantObject, collectionValue }) {
    // Add new item with a unique key combining variant ID and collection
    const uniqueKey = `${variantObject.id}-${collectionValue}`;

    // Check if item already exists
    if (this.items.has(uniqueKey)) {
      // If item exists, increment quantity by the incoming quantity amount
      const existingItem = this.items.get(uniqueKey);
      const currentQuantity = parseInt(existingItem.variantObject.quantity || '1');
      const incomingQuantity = parseInt(variantObject.quantity || '1');
      existingItem.variantObject.quantity = String(currentQuantity + incomingQuantity);
      this.items.set(uniqueKey, existingItem);
    } else {
      // If new item, use the incoming quantity as-is
      variantObject.quantity = variantObject.quantity || '1';
      this.items.set(uniqueKey, { variantObject, collectionValue });
    }

    // Render will handle calculating totals and dispatching events
    await this.render();
    return true; // Return true to indicate the add was successful
  }

  removeItem(itemId) {
    this.items.delete(itemId);
    this.updateCartItemCount();
    this.generateCart();
    this.updateTotals();
  }

  clearCart() {
    this.items.clear();
    this.updateCartItemCount();
    this.generateCart();
    this.updateTotals();
  }

  // Validate that variant IDs still exist by fetching fresh collection data
  async validateVariants() {
    const invalidVariants = [];
    const items = Array.from(this.items.values());
    const bundleBoard = this.closest('bundle-board');

    if (!bundleBoard?.collectionManager) {
      return invalidVariants; // Can't validate without collection manager
    }

    // Group items by collection
    const itemsByCollection = items.reduce((acc, item) => {
      const collection = item.collectionValue;
      if (!acc[collection]) acc[collection] = [];
      acc[collection].push(item);
      return acc;
    }, {});

    // Fetch fresh data for each collection and validate variants
    await Promise.all(
      Object.entries(itemsByCollection).map(async ([collectionHandle, collectionItems]) => {
        try {
          // Clear cache and fetch fresh collection data
          bundleBoard.collectionManager.collections.delete(collectionHandle);
          const collectionData = await bundleBoard.collectionManager.fetchAllProducts(collectionHandle);

          if (!collectionData?.products) {
            // Collection doesn't exist - mark all variants as invalid
            collectionItems.forEach((item) => invalidVariants.push(item.variantObject.id));
            return;
          }

          // Build set of all valid variant IDs in this collection
          const validVariantIds = new Set();
          collectionData.products.forEach((product) => {
            product.variants.forEach((variant) => {
              if (variant.available) {
                validVariantIds.add(String(variant.id));
              }
            });
          });

          // Check each item against valid variants
          collectionItems.forEach((item) => {
            if (!validVariantIds.has(String(item.variantObject.id))) {
              invalidVariants.push(item.variantObject.id);
            }
          });
        } catch (error) {
          console.warn(`Could not validate collection ${collectionHandle}:`, error);
        }
      })
    );

    return invalidVariants;
  }

  generateCart() {
    const cartHTML = this.generateCartHTML();
    this.cartItemsContainer.innerHTML = cartHTML;
    this.updateTotals();
  }
}

customElements.define('bundle-cart', BundleCart);
customElements.define('bundle-board', BundleBoard);
customElements.define('bundle-product', BundleProduct);

// Helper method to generate quantity selector HTML for curtains
function generateQuantitySelector(uniqueId) {
  return `
    <div>
      <legend class="quantity__label form__label nb-body-copy-11-regular" style="margin-bottom: 0;">
        Quantity
      </legend>
      <div class="nb-bundle-collection-item-quantity">
        <input
          type="radio"
          id="pack-quantity-1-${uniqueId}"
          name="pack-quantity-${uniqueId}"
          value="1"
          class="nb-pack-quantity"
          js-quantity-pack
          checked
        >
        <label for="pack-quantity-1-${uniqueId}"> Individual </label>
        <input
          type="radio"
          id="pack-quantity-2-${uniqueId}"
          name="pack-quantity-${uniqueId}"
          value="2"
          class="nb-pack-quantity"
          js-quantity-pack
        >
        <label for="pack-quantity-2-${uniqueId}"> Set of 2 </label>
      </div>
    </div>
  `;
}

// Helper method to generate standard quantity selector for non-curtain products
function generateStandardQuantitySelector(uniqueId) {
  return `
    <fieldset class="nb-bundle-quantity-selector">
      <legend class="quantity__label form__label nb-body-copy-11-regular" style="margin-bottom: 0;">
        Quantity
      </legend>
      <div class="nb-bundle-quantity-controls">
        <button
          type="button"
          class="nb-quantity-button"
          js-quantity-decrease
          data-id="${uniqueId}"
          aria-label="Decrease quantity"
          aria-controls="quantity-${uniqueId}"
          title="Decrease quantity"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <input
          type="number"
          id="quantity-${uniqueId}"
          name="quantity-${uniqueId}"
          value="1"
          min="1"
          class="nb-quantity-input"
          js-quantity-input
          data-id="${uniqueId}"
          aria-label="Quantity"
          aria-describedby="quantity-label-${uniqueId}"
        >
        <button
          type="button"
          class="nb-quantity-button"
          js-quantity-increase
          data-id="${uniqueId}"
          aria-label="Increase quantity"
          aria-controls="quantity-${uniqueId}"
          title="Increase quantity"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M6 2V10M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </fieldset>
  `;
}

class BundleOnboarding extends HTMLElement {
  constructor() {
    super();

    this.outer = this.querySelector('[js-onboarding-outer]');
    this.content = this.querySelector('[js-onboarding-content]');
    this.close = this.querySelector('[js-onboarding-close]');
    this.open = this.querySelector('[js-onboarding-open]');
  }

  connectedCallback() {
    this.close.addEventListener('click', this.closeOnboarding.bind(this));

    // Add click event listener to handle clicks outside the content
    this.outer.addEventListener('click', (event) => {
      if (this.content && !this.content.contains(event.target) && !event.target.closest('[js-onboarding-close]')) {
        this.closeOnboarding();
      }
    });

    this.open.addEventListener('click', this.openOnboarding.bind(this));
  }

  closeOnboarding() {
    this.setAttribute('data-open', 'false');
    if (typeof window.fireEngagedVisit === 'function') {
      window.fireEngagedVisit('nursery_builder');
    }
  }

  openOnboarding() {
    this.setAttribute('data-open', 'true');
  }
}

customElements.define('bundle-onboarding', BundleOnboarding);
